import {
  getCountries,
  getCountryCallingCode,
  getExampleNumber,
  isValidPhoneNumber,
  validatePhoneNumberLength,
} from 'libphonenumber-js/max'
import mobilePhoneExamples from 'libphonenumber-js/examples.mobile.json'
import { i18n, t } from '@/plugins/i18n/i18n'

/**
 * 預設手機國家 ISO alpha-2 代碼。
 * @type {string}
 */
export const DEFAULT_PHONE_COUNTRY = 'TW'

const TAIWAN_MOBILE_PATTERN = /^0?9\d{8}$/
const TAIWAN_PLACEHOLDER_EXAMPLES = ['0912345678', '912345678']
const DEFAULT_DISPLAY_NAME_LOCALES = ['en']
const DISPLAY_NAME_LOCALE_MAP = {
  zh: ['zh-TW', 'zh-Hant', 'en'],
  en: ['en'],
  jp: ['ja', 'en'],
  ja: ['ja', 'en'],
  kr: ['ko', 'en'],
  ko: ['ko', 'en'],
}
const regionDisplayNamesCache = new Map()
const phoneCountryOptionsCache = new Map()

const getDisplayNameLocales = (locale = i18n.locale) => {
  return DISPLAY_NAME_LOCALE_MAP[locale] || DEFAULT_DISPLAY_NAME_LOCALES
}

const getRegionDisplayNames = (locale = i18n.locale) => {
  if (typeof Intl === 'undefined' || typeof Intl.DisplayNames !== 'function') {
    return null
  }

  const locales = getDisplayNameLocales(locale)
  const cacheKey = locales.join(',')

  if (!regionDisplayNamesCache.has(cacheKey)) {
    regionDisplayNamesCache.set(cacheKey, new Intl.DisplayNames(locales, {
      type: 'region',
      fallback: 'code',
    }))
  }

  return regionDisplayNamesCache.get(cacheKey)
}

/**
 * @typedef {Object} PhoneCountryOption
 * @property {string} value - 國家 ISO alpha-2 代碼
 * @property {string} label - 顯示文字，格式為「國名 (+國碼)」
 * @property {string} countryDisplayName - 國家顯示名稱
 * @property {string} phoneCountryCode - 國際電話國碼
 */

/**
 * @typedef {Object} InternationalPhoneValidationResult
 * @property {boolean} isValid - 是否為有效手機號碼
 * @property {string} message - 驗證訊息
 * @property {string} normalizedPhone - 清理後的原始輸入
 * @property {string} payloadPhone - 要送到後端儲存的手機號碼
 * @property {string} lookupPhone - 要送到查重 API 的手機號碼
 * @property {string} phoneCountryCode - 國際電話國碼
 * @property {string} country - 國家 ISO alpha-2 代碼
 * @property {('TOO_SHORT'|'TOO_LONG'|'INVALID_LENGTH'|'NOT_A_NUMBER'|undefined)} [lengthError] - libphonenumber-js 長度驗證結果
 */

/**
 * 取得國家顯示名稱。
 * @param {string} country - 國家 ISO alpha-2 代碼
 * @param {string} [locale=i18n.locale] - 專案目前語系
 * @returns {string} 在目前語系下的國名，若無法轉換則回傳原始代碼
 */
export const getCountryDisplayName = (country, locale = i18n.locale) => {
  return getRegionDisplayNames(locale)?.of(country) || country
}

/**
 * 取得指定國家的國際電話國碼。
 * @param {string} [country=DEFAULT_PHONE_COUNTRY] - 國家 ISO alpha-2 代碼
 * @returns {string} 例如 `+886`
 */
const getPhoneCountryCode = (country = DEFAULT_PHONE_COUNTRY) => {
  return `+${getCountryCallingCode(country)}`
}

/**
 * 清理電話輸入字串中的空白與常見分隔符號。
 * @param {string|number} [value=''] - 原始電話輸入
 * @returns {string} 清理後的電話字串
 */
const normalizePhoneInput = (value = '') => {
  return String(value).trim().replace(/[\s()-]/g, '')
}

/**
 * 建立國際電話驗證結果物件。
 * @param {Object} params - 驗證結果參數
 * @param {boolean} params.isValid - 是否驗證成功
 * @param {string} params.normalizedPhone - 清理後的電話號碼
 * @param {string} params.phoneCountryCode - 國際電話國碼
 * @param {string} params.country - 國家 ISO alpha-2 代碼
 * @param {string} [params.payloadPhone=params.normalizedPhone] - 要送到後端的號碼
 * @param {string} [params.lookupPhone=''] - 要送到查重 API 的號碼
 * @param {InternationalPhoneValidationResult['lengthError']} [params.lengthError] - 號碼長度錯誤型別
 * @returns {InternationalPhoneValidationResult} 統一格式的驗證結果
 */
const createPhoneValidationResult = ({
  isValid,
  normalizedPhone,
  phoneCountryCode,
  country,
  payloadPhone = normalizedPhone,
  lookupPhone = '',
  lengthError,
}) => {
  return {
    isValid,
    message: isValid ? '' : t('member.tab.memberInfo.invalidPhoneMessage'),
    normalizedPhone,
    payloadPhone,
    lookupPhone,
    phoneCountryCode,
    country,
    ...(lengthError ? { lengthError } : {}),
  }
}

/**
 * 建立所有可選國家的電話國碼選項。
 * @param {string} [locale=i18n.locale] - 專案目前語系
 * @returns {PhoneCountryOption[]} 電話國碼選項列表
 */
const buildPhoneCountryOptions = (locale = i18n.locale) => {
  return getCountries()
    .map((country) => {
      const phoneCountryCode = getPhoneCountryCode(country)
      const countryDisplayName = getCountryDisplayName(country, locale)

      return {
        value: country,
        label: `${countryDisplayName} (${phoneCountryCode})`,
        countryDisplayName,
        phoneCountryCode,
      }
    })
    .sort((left, right) => {
      if (left.value === DEFAULT_PHONE_COUNTRY) return -1
      if (right.value === DEFAULT_PHONE_COUNTRY) return 1

      if (left.phoneCountryCode !== right.phoneCountryCode) {
        return left.phoneCountryCode.localeCompare(right.phoneCountryCode, undefined, {
          numeric: true,
        })
      }

      return left.value.localeCompare(right.value)
    })
}

/**
 * 取得已快取的電話國碼選項。
 * @param {string} [locale=i18n.locale] - 專案目前語系
 * @returns {PhoneCountryOption[]} 電話國碼選項列表
 */
export const getPhoneCountryOptions = (locale = i18n.locale) => {
  if (!phoneCountryOptionsCache.has(locale)) {
    phoneCountryOptionsCache.set(locale, buildPhoneCountryOptions(locale))
  }

  return phoneCountryOptionsCache.get(locale)
}

/**
 * 取得指定國家在 placeholder 使用的本地手機範例。
 * @param {string} [country=DEFAULT_PHONE_COUNTRY] - 國家 ISO alpha-2 代碼
 * @returns {string[]} 本地手機範例
 */
export const getPhoneCountryPlaceholderExamples = (country = DEFAULT_PHONE_COUNTRY) => {
  if (country === DEFAULT_PHONE_COUNTRY) {
    return TAIWAN_PLACEHOLDER_EXAMPLES
  }

  const exampleNumber = getExampleNumber(country, mobilePhoneExamples)
  if (!exampleNumber) {
    return []
  }

  return [exampleNumber.nationalNumber.replace(/^0+/, '')]
}

/**
 * 驗證會員手機號碼，並回傳後端儲存與查重所需的電話格式。
 * @param {Object} params - 驗證參數
 * @param {string|number} params.phone - 原始電話輸入
 * @param {string} [params.country=DEFAULT_PHONE_COUNTRY] - 國家 ISO alpha-2 代碼
 * @returns {InternationalPhoneValidationResult} 統一格式的驗證結果
 */
export const validateInternationalMemberPhone = ({
  phone,
  country = DEFAULT_PHONE_COUNTRY,
}) => {
  const normalizedPhone = normalizePhoneInput(phone)
  const phoneCountryCode = getPhoneCountryCode(country)

  if (!normalizedPhone) {
    return createPhoneValidationResult({
      isValid: false,
      normalizedPhone,
      payloadPhone: '',
      lookupPhone: '',
      phoneCountryCode,
      country,
    })
  }

  if (country === DEFAULT_PHONE_COUNTRY) {
    if (!TAIWAN_MOBILE_PATTERN.test(normalizedPhone)) {
      return createPhoneValidationResult({
        isValid: false,
        normalizedPhone,
        phoneCountryCode,
        country,
      })
    }

    return createPhoneValidationResult({
      isValid: true,
      normalizedPhone,
      lookupPhone: normalizedPhone.startsWith('0') ? normalizedPhone : `0${normalizedPhone}`,
      phoneCountryCode,
      country,
    })
  }

  const lengthError = validatePhoneNumberLength(normalizedPhone, country)
  const isValid = !lengthError && isValidPhoneNumber(normalizedPhone, country)

  return createPhoneValidationResult({
    isValid,
    normalizedPhone,
    lookupPhone: isValid ? normalizedPhone : '',
    phoneCountryCode,
    country,
    lengthError,
  })
}
